/**
 * AdminLayout — sidebar shell for the §10.1 /admin/* routes.
 *
 * NavLink-driven nav so the active section is highlighted automatically.
 * Renders the global Header on top so admins still see their settings
 * controls and logout. Outlet renders the active admin page.
 *
 * The §10.1 admin routes are admin-only on the server (every M3 router
 * checks role); we additionally hide the nav from non-admin users so
 * employees who happen to type /admin/transactions don't see the
 * surface even though they couldn't load anything inside.
 */

import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, Users as UsersIcon, Archive as ArchiveIcon, Settings as SettingsIcon, ScrollText, FileSpreadsheet, LayoutDashboard } from "lucide-react";
import { Header } from "./Header";
import { TopProgressBar } from "./TopProgressBar";
import { useAuthBootstrap, useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";

export function AdminLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  useAuthBootstrap();

  // Mirror the server-side admin guard. RequireAuth already ensured a
  // user is logged in; this check protects the entire admin tree from
  // employees.
  if (user && user.role !== "ADMIN") {
    return <Navigate to="/dashboard" replace />;
  }

  const links = [
    { to: "/admin/transactions", label: t("admin.transactions"), Icon: Briefcase },
    { to: "/admin/users", label: t("admin.users"), Icon: UsersIcon },
    { to: "/admin/import", label: t("admin.import"), Icon: FileSpreadsheet },
    { to: "/admin/archive", label: t("admin.archive"), Icon: ArchiveIcon },
    { to: "/admin/settings", label: t("admin.settings"), Icon: SettingsIcon },
    { to: "/admin/audit", label: t("admin.audit"), Icon: ScrollText },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-canvas dark:bg-canvas-dark">
      <TopProgressBar />
      <Header />
      <div className="mx-auto w-full max-w-7xl px-4 py-4 flex-1 flex flex-col md:flex-row gap-4">
        <nav className="md:w-56 shrink-0 rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-2">
          {/* "Back to dashboard" entry sits ABOVE the admin section with a
              hairline separator below it on desktop. The admin pages are
              tools; the dashboard is the main view — staff should reach
              it in one click without using the browser back button. */}
          <ul className="flex md:flex-col gap-1 overflow-x-auto">
            <li>
              <NavLink
                to="/dashboard"
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap text-muted dark:text-muted-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
              >
                <LayoutDashboard className="h-4 w-4" aria-hidden />
                {t("common.dashboard")}
              </NavLink>
            </li>
            <li
              role="separator"
              aria-hidden
              className="hidden md:block my-1 border-t border-hairline dark:border-hairline-dark"
            />
            {links.map(({ to, label, Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap ${
                      isActive
                        ? "bg-fg/10 dark:bg-fg-dark/10 text-fg dark:text-fg-dark font-semibold"
                        : "text-muted dark:text-muted-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
